<script setup lang="ts">
import ThreadsNav from "@/components/mobile/ThreadsNav.vue";
import { computed, onMounted, ref } from "vue";

import MediaSelectPopup from "@/components/mobile/media/MediaSelectPopup.vue";
import { ThreadListInstance } from "@/components/mobile/ThreadList.vue";
import { useScopedMateId } from "@/composable/useScopedMateId";
import api from "@/modules/api";
import { downloadImage } from "@/modules/browser/download";
import { inApp } from "@/modules/common";
import { useGenerate } from "@/stores/generate";
import { useGlobalUser } from "@/stores/globalUser";
import { MateSelectorParam, useUI } from "@/stores/ui";
import { MediaUploadFileItem } from "@/types/component";
import { Mate, Media } from "@/types/entity";
import { useDebounceFn, useIntervalFn } from "@vueuse/core";
import { useRouteQuery } from "@vueuse/router";
import {
  showConfirmDialog,
  showDialog,
  showLoadingToast,
  showSuccessToast,
} from "vant";
import { SwipeInstance } from "vant/es/swipe";
import { nextTick, watch } from "vue";
import { useI18n } from "vue-i18n";
import MediaGalleryOverlay from "@/components/mobile/gallery/MediaGalleryOverlay.vue";
import { onBeforeRouteLeave } from "vue-router";

const generateStore = useGenerate();
const uiStore = useUI();
const userStore = useGlobalUser();
const { t } = useI18n();

const mode = useRouteQuery<"history" | undefined>("mode", undefined);

// 选择参考图
const showMediaSelectPopup = ref(false);
const referenceMedia = ref<MediaUploadFileItem | null>(null);
const currentIndex = ref(0);
const swipeRef = ref<SwipeInstance | null>(null);
const currentTaskUUIDs = ref<string[]>([]);
const currentTaskList = computed(() => {
  return currentTaskUUIDs.value
    .map((uuid) => generateStore.getTaskEntity(uuid))
    .filter((v) => v !== undefined);
});
const pendingTaskList = computed(() => {
  return currentTaskList.value.filter((task) => task.mediaId === null);
});

// 历史记录
const showHistoryPopup = ref(false);
const historyListRef = ref<ThreadListInstance | null>(null);

// 图片详情
const idx = ref(0);
const showImageDetailPopup = ref(false);
const targetMedia = computed<Media | null>(() => {
  return targetTask.value?.resultMediazz[idx.value] ?? null;
});
const targetTask = ref<TextImage | null>(null);

onMounted(async () => {
  await generateStore.initBeautifyTaskListIfAbsent(true);
});

const debouncedRefreshTask = useDebounceFn(async () => {
  if (pendingTaskList.value?.length === 0) return;
  for (const task of pendingTaskList.value) {
    await generateStore.readTaskByUuid(task.uuid);
  }
});

useIntervalFn(async () => {
  await debouncedRefreshTask();
}, 3000);

watch(showHistoryPopup, async (val) => {
  if (!val) false;
  await nextTick();
  historyListRef.value?.onPullRefresh();
});

watch(
  mode,
  (val) => {
    if (val === "history") {
      showHistoryPopup.value = true;
      mode.value = undefined;
    }
  },
  {
    immediate: true,
  }
);

const handleMediaSelected = async (mediaList: MediaUploadFileItem[]) => {
  referenceMedia.value = mediaList[0];
};

const handleSwipeChange = (index: number) => {
  currentIndex.value = index;
};

const beautifyImage = async () => {
  if (!referenceMedia.value) {
    showDialog({
      title: "提示",
      message: "请先点击选择图片来设置参考图后生成美颜图",
    });
    return;
  }
  const task = await generateStore.createBeautifyTask(
    referenceMedia.value.mediaId
  );
  currentTaskUUIDs.value.push(task.taskId);
  swipeRef.value?.swipeTo(currentTaskUUIDs.value.length);
  await userStore.refreshUser();
};

const onRefreshList = async () => {
  await generateStore.initBeautifyTaskListIfAbsent(true);
  await nextTick();
  historyListRef.value?.resetScrollLoadingState();
};

const clickMediaDetail = (mediaIdx: number, task: TextImage) => {
  idx.value = mediaIdx;
  targetTask.value = task;
  showImageDetailPopup.value = true;
};

const saveToGallery = async (media: Media) => {
  const param: MateSelectorParam = {
    selectedMateId: -1,
    mateOnly: false,
    selectionMode: "selection",
    title: t("Message.uploadToWhichAlbum"),
    switchMate: false,
    callback: (mate: Mate) => {
      const scopeFunc = useScopedMateId(async () => {
        const loading = showLoadingToast({
          message: t("Message.saving"),
        });
        try {
          await api.createOneMateMedia({
            mediaId: media.id,
            type: 1,
          });
          loading.close();
          await nextTick();
          showSuccessToast(t("Message.saveSuccess"));
          uiStore.showMateSelector = false;
        } finally {
          loading.close();
        }
      }, mate.id);
      scopeFunc();
    },
  };
  uiStore.popMateSelector(param);
};

const deleteTask = async (uuid: string) => {
  try {
    await showConfirmDialog({
      title: t("Message.delete"),
      message: t("Message.deleteConfirm"),
      confirmButtonText: t("Buttons.confirm"),
      cancelButtonText: t("Buttons.cancel"),
    });
    const loading = showLoadingToast(t("Message.deleting"));
    try {
      await generateStore.deleteTaskByUuid(uuid);
      await nextTick();
      historyListRef.value?.resetScrollLoadingState();
    } finally {
      loading.close();
    }
  } catch {
    return;
  }
};

onBeforeRouteLeave(() => {
  if (showImageDetailPopup.value) {
    showImageDetailPopup.value = false;
    return false;
  }
  if (showHistoryPopup.value) {
    showHistoryPopup.value = false;
    return false;
  }
  return true;
});
</script>

<template>
  <div class="grid grid-rows-[auto_1fr] h-full">
    <ThreadsNav show-back class="bg-base-300">
      <template #center>
        <div></div>
      </template>
      <template #right>
        <CrystalPowerCount />
      </template>
    </ThreadsNav>
    <div class="p-2 flex flex-col w-full overflow-hidden">
      <div class="flex-1 grid grid-rows-[1fr_auto] max-h-[65%]">
        <van-swipe
          ref="swipeRef"
          class="h-full"
          indicator-color="white"
          :loop="false"
          :initial-swipe="0"
          :show-indicators="false"
          @change="handleSwipeChange"
        >
          <van-swipe-item>
            <div
              class="bg-base-300 h-full flex items-center justify-center"
              @click="showMediaSelectPopup = true"
            >
              <button
                v-if="!referenceMedia"
                class="btn btn-outline btn-primary"
              >
                选择图片
              </button>
              <div v-else class="w-full h-full relative">
                <div
                  class="absolute bottom-1 right-1 border p-1 rounded text-xs bg-black/80 text-white border-white/50"
                >
                  参考图
                </div>
                <img
                  :src="referenceMedia.url"
                  class="w-full h-full object-contain"
                />
              </div>
            </div>
          </van-swipe-item>
          <van-swipe-item v-for="task in currentTaskList" :key="task.id">
            <div class="h-full grid grid-cols-2 gap-0.5 grid-rows-2">
              <template v-if="!task.mediaId">
                <div
                  v-for="i in 4"
                  :key="i"
                  class="skeleton-loading-bg h-full w-full bg-base-300 flex items-center justify-center"
                >
                  <span class="loading loading-spinner loading-xs"></span>
                </div>
              </template>
              <template v-else>
                <VanImage
                  v-for="(media, mediaIdx) in task.resultMediazz"
                  :key="media.id"
                  :src="media.url"
                  @click="clickMediaDetail(mediaIdx, task)"
                  fit="contain"
                  class="w-full h-full bg-base-300"
                />
              </template>
            </div>
          </van-swipe-item>
        </van-swipe>
      </div>
      <div
        class="flex gap-1 items-center justify-centers pt-4 w-full"
        :class="{
          invisible: currentTaskList.length === 0,
        }"
      >
        <div class="flex gap-1 items-center justify-center w-full">
          <div
            v-for="i in currentTaskList.length + 1"
            :key="i"
            class="w-2.5 h-2.5 rounded-full cursor-pointer"
            :class="{
              'bg-primary ': i === currentIndex + 1,
              'border border-base-content': i !== currentIndex + 1,
            }"
            @click="swipeRef?.swipeTo(i - 1)"
          />
        </div>
      </div>
      <div
        class="flex-[0.6] flex flex-col items-center justify-center gap-5 relative"
      >
        <div class="flex flex-col items-center justify-center gap-1">
          <button class="btn btn-circle btn-primary" @click="beautifyImage">
            <i class="iconify mingcute--lightning-line" />
          </button>
          <div class="text-sm flex gap-1">
            2<PowerIcon class="relative top-0.5" />
          </div>
        </div>
        <button
          class="btn btn-ghost flex-col gap-1 justify-center items-center"
          @click="showHistoryPopup = true"
        >
          <i class="iconify ic--baseline-history" />
          <span class="text-xs">{{ $t("Buttons.history") }}</span>
        </button>
        <div
          v-if="pendingTaskList.length > 0"
          class="absolute bottom-0 left-0 right-0 text-center text-xs text-base-content/50"
        >
          生成时间约为20-30秒
        </div>
      </div>
    </div>
    <MediaGalleryOverlay
      v-if="showImageDetailPopup"
      v-model:idx="idx"
      v-model:show="showImageDetailPopup"
      :list="targetTask?.resultMediazz ?? []"
      :next-page-func="() => false"
    >
      <template #buttons>
        <button
          class="btn btn-xs"
          @click="saveToGallery(targetMedia!)"
          v-if="targetMedia"
        >
          {{ $t("Message.saveToAlbum") }}
        </button>
        <button
          v-if="!inApp"
          class="btn btn-xs"
          @click="downloadImage(targetMedia!.url!)"
        >
          {{ $t("Buttons.download") }}
        </button>
      </template>
      <template #tips>
        <div class="text-center text-xs text-base-content/50">
          长按图片保存到相册
        </div>
      </template>
    </MediaGalleryOverlay>
    <MediaSelectPopup
      v-model:show="showMediaSelectPopup"
      :media-type="'img'"
      :multiple="false"
      :upload-type="7"
      :enable-mate-gallery="true"
      enable-use-avatar
      enable-crop
      :crop-aspect-ratio="3 / 4"
      replace
      @on-media-selected="handleMediaSelected"
    />
    <VanPopup
      v-if="showHistoryPopup"
      v-model:show="showHistoryPopup"
      position="bottom"
      class="h-full overflow-hidden grid grid-rows-[auto_1fr]"
    >
      <ThreadsNav :show-back="false" :show-left-logo="false">
        <template #left>
          <button
            class="btn btn-ghost btn-square"
            @click.stop="showHistoryPopup = false"
          >
            <light-arrow-icon class="text-base" />
          </button>
        </template>
        <template #center>
          <div>{{ $t("Buttons.history") }}</div>
        </template>
      </ThreadsNav>
      <div class="h-full w-full overflow-hidden">
        <ThreadList
          v-if="showHistoryPopup"
          :onPullLoad="onRefreshList"
          :onScrollLoad="generateStore.fetchOlderBeautifyTaskList"
          :immediate-check="false"
          enable-back-top
          ref="historyListRef"
          class="h-full w-full"
        >
          <div class="flex flex-col gap-4 p-2">
            <div
              v-for="task in generateStore.beautifyTaskList"
              :key="task.id"
              class="flex flex-col gap-1"
            >
              <div class="flex justify-between items-center">
                <span>{{ $t("SidebarNavi.beautify") }}</span>
                <button
                  class="btn btn-ghost btn-xs"
                  @click="deleteTask(task.uuid!)"
                  v-if="task.mediaId"
                >
                  <i class="iconify mingcute--delete-2-line" />
                </button>
              </div>
              <div class="grid grid-cols-4 gap-1">
                <template v-if="!task.mediaId">
                  <div
                    v-for="i in 4"
                    :key="i"
                    class="skeleton-loading-bg w-full bg-base-300 flex items-center justify-center aspect-[3/4]"
                  >
                    <span class="loading loading-spinner loading-xs"></span>
                  </div>
                </template>
                <template v-else>
                  <div
                    v-for="(media, mediaIdx) in task.resultMediazz"
                    :key="media.id"
                    class="w-full aspect-[3/4]"
                  >
                    <VanImage
                      class="w-full h-full"
                      :src="media.url"
                      @click="clickMediaDetail(mediaIdx, task)"
                    />
                  </div>
                </template>
              </div>
            </div>
          </div>
        </ThreadList>
      </div>
    </VanPopup>
  </div>
</template>
